import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

// Supported currencies with their symbols and formatting
const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: 'US$', flag: '🇺🇸', locale: 'en-US', timezones: ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'America/Honolulu', 'America/Detroit', 'America/Indiana/Indianapolis', 'America/Kentucky/Louisville', 'America/Boise'] },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', flag: '🇨🇦', locale: 'en-CA', timezones: ['America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Halifax', 'America/St_Johns', 'America/Montreal', 'America/Regina'] },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', flag: '🇭🇰', locale: 'zh-HK', timezones: ['Asia/Hong_Kong'] },
  { code: 'MOP', name: 'Macau Pataca', symbol: 'MOP$', flag: '🇲🇴', locale: 'zh-MO', timezones: ['Asia/Macau'] },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳', locale: 'zh-CN', timezones: ['Asia/Shanghai', 'Asia/Chongqing', 'Asia/Harbin', 'Asia/Urumqi', 'Asia/Kashgar'] },
];

// Detect currency from browser timezone
const detectCurrencyFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Find exact match first
    for (const currency of CURRENCIES) {
      if (currency.timezones.includes(timezone)) {
        return currency.code;
      }
    }
    
    // Fallback: check if timezone starts with region
    if (timezone.startsWith('Asia/Hong')) return 'HKD';
    if (timezone.startsWith('Asia/Macau') || timezone.startsWith('Asia/Macao')) return 'MOP';
    if (timezone.startsWith('Asia/Shanghai') || timezone.startsWith('Asia/')) return 'CNY';
    if (timezone.startsWith('America/Toronto') || timezone.startsWith('America/Vancouver') || 
        timezone.startsWith('America/Edmonton') || timezone.startsWith('America/Winnipeg') ||
        timezone.startsWith('America/Halifax') || timezone.startsWith('America/Montreal')) return 'CAD';
    if (timezone.startsWith('America/')) return 'USD';
    
    // Default to HKD (Hong Kong)
    return 'HKD';
  } catch {
    return 'HKD';
  }
};

// Format price with currency symbol
export const formatPrice = (amount, currencyCode) => {
  const currency = CURRENCIES.find(c => c.code === currencyCode);
  if (!currency) return `$${parseFloat(amount || 0).toFixed(2)}`;
  return `${currency.symbol}${parseFloat(amount || 0).toFixed(2)}`;
};

// Get currency symbol by code
export const getCurrencySymbol = (currencyCode) => {
  const currency = CURRENCIES.find(c => c.code === currencyCode);
  return currency ? currency.symbol : '$';
};

const CurrencyInput = ({ 
  value = '', 
  currency = null,
  onValueChange,
  onCurrencyChange,
  placeholder = '0.00',
  className = '',
  disabled = false,
  required = false,
  id,
  name,
  'data-testid': dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    // Initialize currency from prop or auto-detect
    if (currency) {
      const found = CURRENCIES.find(c => c.code === currency);
      if (found) return found;
    }
    // Auto-detect from timezone
    const detectedCode = detectCurrencyFromTimezone();
    return CURRENCIES.find(c => c.code === detectedCode) || CURRENCIES[0];
  });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const initializedRef = useRef(false);

  // Notify parent of initial currency selection (only once)
  useEffect(() => {
    if (!initializedRef.current && onCurrencyChange && selectedCurrency) {
      initializedRef.current = true;
      onCurrencyChange(selectedCurrency.code);
    }
  }, [onCurrencyChange, selectedCurrency]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle currency selection
  const handleCurrencySelect = (curr) => {
    setSelectedCurrency(curr);
    setIsOpen(false);
    onCurrencyChange?.(curr.code);
    inputRef.current?.focus();
  };

  // Handle price input change
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    // Allow only numbers and one decimal point
    const sanitized = inputValue.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    onValueChange?.(sanitized);
  };

  return (
    <div className={`flex ${className}`} ref={dropdownRef}>
      {/* Currency Selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`flex items-center gap-1 px-3 py-2 border border-r-0 rounded-l-md bg-gray-50 hover:bg-gray-100 transition-colors min-w-[90px] justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          data-testid={dataTestId ? `${dataTestId}-currency-selector` : undefined}
        >
          <span className="flex items-center gap-1">
            <span className="text-lg">{selectedCurrency?.flag}</span>
            <span className="text-sm font-medium text-gray-700">{selectedCurrency?.symbol}</span>
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-52 bg-white border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
            {CURRENCIES.map((curr) => (
              <button
                key={curr.code}
                type="button"
                onClick={() => handleCurrencySelect(curr)}
                className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 transition-colors text-left ${selectedCurrency?.code === curr.code ? 'bg-yellow-50' : ''}`}
              >
                <span className="text-lg">{curr.flag}</span>
                <span className="flex-1 text-sm">{curr.name}</span>
                <span className="text-sm font-medium text-gray-600">{curr.symbol}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Price Input */}
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        id={id}
        name={name}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={`flex-1 px-3 py-2 border rounded-r-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        data-testid={dataTestId}
      />
    </div>
  );
};

export { CURRENCIES, detectCurrencyFromTimezone };
export default CurrencyInput;
